import { apiService, type PagingApiAndNotifierConfig } from "@modernary/mui/utils/api";
import { serializeQueryParams } from "@modernary/mui/utils/helpers";
import { ToastOptionsSuccess, ToastOptionsError } from "@modernary/mui/components/toasts";
import { apiHosts } from "@/api/constants/apiHosts";
import type {
  ComplaintInvestigation,
  ComplaintInvestigationNew,
  ComplaintInvestigationQuery,
} from "../inspections/inspection";
import complaintInvestigationService from "./complaintInvestigationService";
import type { AxiosResponse } from "axios";
import { LE } from "@/constants/enums";

const COMP_INV_URL = "inspections/complaints"; //Dedicated endpoint specifically for complaint investigations
const URL = "inspections"; //Shared endpoint for the rest of investigation/inspection endpoints
const ENTITY_NAME = "Complaint Investigation";

const axiosParams = {
  // Set different base URL based on the environment
  baseURL: import.meta.env.VITE_AQMS_API_BASE_URL,
};
const api = apiService<ComplaintInvestigation>(apiHosts.AQMS, axiosParams);

const getComplaintInvestigations = (params: ComplaintInvestigationQuery, config: PagingApiAndNotifierConfig = {}) => {
  const successToastCb = (options: ToastOptionsSuccess) => {
    options.hide = true;
    config?.successToastCb?.(options);
  };
  const errorToastCb = (options: ToastOptionsError) => {
    options.fetched = ENTITY_NAME;
    config?.errorToastCb?.(options);
  };

  const url = config.pagingUrl ? config.pagingUrl : COMP_INV_URL;

  return api.getPaged(url, {
    params,
    paramsSerializer: serializeQueryParams,
    abort: config?.abortMethod,
    successToastCb,
    errorToastCb,
  });
};

const getComplaintInvestigationById = (id: string | number, config: PagingApiAndNotifierConfig = {}) => {
  const successToastCb = (options: ToastOptionsSuccess) => {
    options.hide = true;
    config?.successToastCb?.(options);
  };
  const errorToastCb = (options: ToastOptionsError) => {
    options.fetched = ENTITY_NAME;
    config?.errorToastCb?.(options);
  };

  return api.get(URL + "/" + id, {
    successToastCb,
    errorToastCb,
  });
};

const getNewComplaintInvestigation = async (ids: ComplaintInvestigationIds) => {
  let dustControlInspection = {
    isRain: false,
    isPcfSubmitted: false,
    hasTrackout: false,
    hasValidDustPermit: true,
  };
  const defaultApp = {
    data: {
      ...complaintInvestigationService.getInitialData(),
      ...ids,
      vacantLandInspection: {},
      dustControlInspection: dustControlInspection,
      categoryMurn: LE.InspectionCategory.Investigation,
    },
  } as AxiosResponse<ComplaintInvestigationNew, any>;

  return defaultApp;
};

type ComplaintInvestigationIds = {
  id?: string | number;
};
const getComplaintInvestigationByIdOrNew = (
  ids: ComplaintInvestigationIds,
  config: PagingApiAndNotifierConfig = {},
) => {
  if (!ids.id) {
    return getNewComplaintInvestigation(ids);
  } else {
    return getComplaintInvestigationById(ids.id, config) as Promise<AxiosResponse<ComplaintInvestigationNew, any>>;
  }
};

const createComplaintInvestigation = (inspection: ComplaintInvestigation, config: PagingApiAndNotifierConfig = {}) => {
  const successToastCb = (options: ToastOptionsSuccess) => {
    options.created = ENTITY_NAME;
    config?.successToastCb?.(options);
  };
  const errorToastCb = (options: ToastOptionsError) => {
    options.created = ENTITY_NAME;
    config?.errorToastCb?.(options);
  };

  return api.post(URL, inspection, {
    successToastCb,
    errorToastCb,
  });
};

const updateComplaintInvestigation = (inspection: ComplaintInvestigation, config: PagingApiAndNotifierConfig = {}) => {
  const successToastCb = (options: ToastOptionsSuccess) => {
    options.updated = ENTITY_NAME;
    config?.successToastCb?.(options);
  };
  const errorToastCb = (options: ToastOptionsError) => {
    options.updated = ENTITY_NAME;
    config?.errorToastCb?.(options);
  };

  return api.put(URL + "/" + inspection.id, inspection, {
    successToastCb,
    errorToastCb,
  });
};

/**
 * @param {ComplaintInvestigation} inspection This cannot be a 'ref' to a inspection
 */
const saveComplaintInvestigation = (inspection: ComplaintInvestigation, config = {}) => {
  if (inspection.id) {
    return updateComplaintInvestigation(inspection, config);
  } else {
    return createComplaintInvestigation(inspection, config);
  }
};

const deleteComplaintInvestigationById = (id: string | number, config: PagingApiAndNotifierConfig = {}) => {
  const successToastCb = (options: ToastOptionsSuccess) => {
    options.deleted = ENTITY_NAME;
    config?.successToastCb?.(options);
  };
  const errorToastCb = (options: ToastOptionsError) => {
    options.deleted = ENTITY_NAME;
    config?.errorToastCb?.(options);
  };

  return api.delete(URL + "/" + id, {
    successToastCb,
    errorToastCb,
  });
};

export {
  deleteComplaintInvestigationById,
  getComplaintInvestigations,
  getComplaintInvestigationById,
  getNewComplaintInvestigation,
  getComplaintInvestigationByIdOrNew,
  createComplaintInvestigation,
  updateComplaintInvestigation,
  saveComplaintInvestigation,
};
